import { useState, useCallback } from 'react';
import Section from '../base/Section';
import Card from '../base/Card';

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'María González',
      location: 'Granollers Centro',
      rating: 5,
      text: 'Excelente servicio de vaciado de piso tras el fallecimiento de mi madre. Fueron muy respetuosos y discretos en todo momento. El equipo de JUSTCLEAN hizo un trabajo impecable en tiempo récord.',
      avatar: 'MG'
    },
    {
      name: 'Josep Martínez',
      location: 'Canovelles',
      rating: 5,
      text: 'Necesitaba vaciar un local comercial urgentemente y JUSTCLEAN respondió en 24 horas. Presupuesto ajustado, trabajo rápido y profesional. Totalmente recomendables para vaciado de locales en Vallès Oriental.',
      avatar: 'JM'
    },
    {
      name: 'Laura Sánchez',
      location: 'Les Franqueses del Vallès',
      rating: 5,
      text: 'Contraté sus servicios para un caso de síndrome de Diógenes. La sensibilidad y profesionalidad con la que trataron la situación fue excepcional. Gracias por vuestra ayuda en un momento tan difícil.',
      avatar: 'LS'
    },
    {
      name: 'Carlos Ruiz',
      location: 'Parets del Vallès',
      rating: 5,
      text: 'Servicio de vaciado de piso heredado. Muy contentos con el resultado. Retiraron todos los muebles y electrodomésticos, dejaron el piso impecable. Precio justo y trato excelente.',
      avatar: 'CR'
    },
    {
      name: 'Anna Ferrer',
      location: 'La Roca del Vallès',
      rating: 5,
      text: 'Rápidos, eficientes y muy profesionales. Vaciaron mi trastero en Granollers en una mañana. El equipo fue puntual y cuidadoso. Sin duda los volveré a contratar si necesito este tipo de servicios.',
      avatar: 'AF'
    },
    {
      name: 'David López',
      location: 'Montmeló',
      rating: 5,
      text: 'Necesitaba vaciar la casa de mis abuelos y JUSTCLEAN se encargó de todo. Desde la retirada de muebles hasta la limpieza final. Servicio completo y muy recomendable en toda la zona del Vallès Oriental.',
      avatar: 'DL'
    }
  ];

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + 3);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  return (
    <Section background="light" padding="lg">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
          Lo que dicen nuestros clientes
        </h2>
        <p className="text-lg text-tertiary">
          Opiniones reales de clientes satisfechos en Granollers y Vallès Oriental
        </p>
      </div>

      {/* Desktop: 3 columnas */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-8 mb-8">
        {visibleTestimonials.map((testimonial, index) => (
          <Card key={`${currentIndex}-${index}`} className="relative">
            <div className="absolute top-4 right-4 text-6xl text-primary/10 font-serif select-none" aria-hidden="true">"</div>
            
            <div className="flex gap-1 mb-4" role="img" aria-label={`${testimonial.rating} estrellas`}>
              {[...Array(testimonial.rating)].map((_, i) => (
                <i key={i} className="ri-star-fill text-yellow-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              ))}
            </div>

            <p className="text-secondary mb-6 leading-relaxed relative z-10">
              {testimonial.text}
            </p>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold" aria-hidden="true">
                {testimonial.avatar}
              </div>
              <div>
                <p className="font-bold text-secondary">{testimonial.name}</p>
                <p className="text-sm text-tertiary">{testimonial.location}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Mobile/Tablet: Carrusel */}
      <div className="lg:hidden relative">
        <Card className="mb-8">
          <div className="absolute top-4 right-4 text-6xl text-primary/10 font-serif select-none" aria-hidden="true">"</div>
          
          <div className="flex gap-1 mb-4" role="img" aria-label={`${testimonials[currentIndex].rating} estrellas`}>
            {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
              <i key={i} className="ri-star-fill text-yellow-500 text-lg w-5 h-5 flex items-center justify-center"></i>
            ))}
          </div>

          <p className="text-secondary mb-6 leading-relaxed relative z-10">
            {testimonials[currentIndex].text}
          </p>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold" aria-hidden="true">
              {testimonials[currentIndex].avatar}
            </div>
            <div>
              <p className="font-bold text-secondary">{testimonials[currentIndex].name}</p>
              <p className="text-sm text-tertiary">{testimonials[currentIndex].location}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Controles de navegación */}
      <div className="flex justify-center items-center gap-4">
        <button
          onClick={prevSlide}
          className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-primary hover:text-white transition-colors duration-300 cursor-pointer"
          aria-label="Testimonio anterior"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        
        <div className="flex gap-2" role="tablist" aria-label="Navegación de testimonios">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-gray-300 w-2'
              }`}
              role="tab"
              aria-label={`Ir a testimonio ${index + 1}`}
              aria-selected={index === currentIndex}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-primary hover:text-white transition-colors duration-300 cursor-pointer"
          aria-label="Siguiente testimonio"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      </div>
    </Section>
  );
}
